import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { GetMeQuery } from '@/generated/gql/graphql';
import { enterpriseAppName } from '@/constants';
import useEnablePricing from '@/enterprise/components/OfferBuilder/useEnablePricing';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import apiClient from '@/lib/apiClient';
import { isTwaActive } from '@/lib/useTrackTwaSession';
import { getSessionTrackingData } from '@/reporting/sessionTrackingData';
import useUserDetails from '@/shared/data/useUserDetails';
import useAccountFeatureConfigs from '@/shared/providers/useAccountFeatureConfigs';
import { useToken } from '@/utils/token';
import useDataDog from './DataDog';
import { EnterpriseTrackEventNames } from './trackEventNames';
import { C2foPayload, ClientMeta, ReportAttributes } from './types';

export const mutationKeyTrack = ['track'];

export function useReporting() {
  const { tokenContent } = useToken();
  const authServiceUuid = tokenContent?.payload?.user?.uuid ?? null;
  const sessionUuid = tokenContent?.payload?.user?.sessionUuid ?? null;
  const { addAction: dataDogAddAction } = useDataDog();
  const { wasSmbMigrated } = useAccountFeatureConfigs();
  const unificationEnabled = useEnableUnification();
  const pricingEnabled = useEnablePricing();
  const {
    data: { accountId, segment },
  } = useUserDetails(
    useCallback(
      (user: GetMeQuery) => ({
        accountId: user.me?.account?.id,
        segment: user.me?.account?.segment,
      }),
      []
    )
  );
  const sessionTrackingData = useMemo(() => getSessionTrackingData(), []);
  const uri = window?.location?.href ?? null;
  const uriQueryParams = window?.location?.search ?? null;
  const uriStateName = window?.location?.pathname ?? null;
  const clientMeta = useMemo<ClientMeta>(
    () => ({
      createdDateTime: new Date(),
      sessionUuid,
      uriStateName,
      uri,
      uriQueryParams,
      ...sessionTrackingData,
    }),
    [sessionUuid, sessionTrackingData, uri, uriQueryParams, uriStateName]
  );

  const { mutateAsync: postTrack } = useMutation({
    mutationKey: mutationKeyTrack,
    mutationFn: async (payload: C2foPayload) => {
      // during development, log out what will be sent to the analytics api
      if (import.meta.env.MODE === 'development') {
        console.log('track event', { [payload.eventName]: payload });
      }

      if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
        return apiClient.post('api/analytics/track', {
          json: payload,
        });
      }
    },
  });

  /**
   * Internal method, do not export this directly.
   */
  const track = useCallback(
    async (event: EnterpriseTrackEventNames, attributes?: ReportAttributes) => {
      if (authServiceUuid) {
        const namespacedEvent = `enterprise::${event}`;

        const properties: ReportAttributes = {
          clientMeta,
          ...(attributes && { eventMeta: attributes }),
          ...(tokenContent?.payload.impersonationSubject?.uuid && {
            impersonationSubjectUuid: tokenContent.payload.impersonationSubject.uuid,
          }),
          accountId,
          segment,
          wasSmbMigrated,
          unificationEnabled,
          pricingEnabled,
          isTwa: isTwaActive(),
        };

        const payload: C2foPayload = {
          appId: enterpriseAppName,
          authServiceUuid,
          eventName: namespacedEvent,
          properties,
          sendToAtlasQueue: true,
          sessionUuid: clientMeta.sessionUuid,
        };

        try {
          dataDogAddAction(namespacedEvent, { ...properties });
          await postTrack({ ...payload, properties: { ...properties, sessionUuid: clientMeta.sessionUuid } });
        } catch {
          // Don't block app functionality if tracking fails for some reason.
        }
      }
    },
    [
      authServiceUuid,
      accountId,
      clientMeta,
      dataDogAddAction,
      postTrack,
      pricingEnabled,
      segment,
      tokenContent,
      unificationEnabled,
      wasSmbMigrated,
    ]
  );

  /**
   * Sends an enterprise track event. Callers may ignore the returned promise; await it only when
   * the page is about to unload, since a navigation aborts the in-flight request. It never rejects
   * — `track` swallows its own failures — but it also has no timeout, so cap any wait on it.
   */
  const trackEnterpriseEvent = useCallback(
    (event: EnterpriseTrackEventNames, attributes?: ReportAttributes): Promise<void> => track(event, attributes),
    [track]
  );

  return { trackEnterpriseEvent };
}
